import { OVERLAY_KINDS, OVERLAY_PHASES } from '../../overlay_lifecycle/overlay_lifecycle_events'
import {
	buildNextState,
	cloneState,
	createInitialState,
	emitPhaseChanged,
	getDataValue,
	getFirstDomElement,
	installOverlayLifecyclePublicApi,
	isOpenPhase,
	shouldEmit,
	startsNewTransition,
} from '../../overlay_lifecycle/overlay_lifecycle_helpers'

// Page overlays live in frontend/page_overlay. This controller only listens to
// page-overlay events from overlays.js and exposes their normalized lifecycle
// state to other modules.
let pageTransitionId = 0
let lifecycleListenersInstalled = false
let pageOverlayState = createInitialState(OVERLAY_KINDS.PAGE)

const getPageMeta = function ($overlay) {
	const domElement = getFirstDomElement($overlay)

	return {
		id: getDataValue($overlay, 'id'),
		type: getDataValue($overlay, 'type') || 'page',
		link: getDataValue($overlay, 'link'),
		title: getDataValue($overlay, 'title'),
		hasElement: !!domElement,
	}
}

const setPagePhase = function (phase, options = {}) {
	const meta = getPageMeta(options.overlay)
	let transitionId = pageOverlayState.transitionId

	if (options.transitionId) {
		transitionId = options.transitionId
		pageTransitionId = Math.max(pageTransitionId, transitionId)
	} else if (startsNewTransition(pageOverlayState, phase)) {
		pageTransitionId += 1
		transitionId = pageTransitionId
	}

	const previousState = pageOverlayState
	const nextState = buildNextState(pageOverlayState, phase, meta, transitionId, !!options.clearMeta)
	const emit = shouldEmit(previousState, nextState)
	pageOverlayState = nextState

	if (emit) {
		emitPhaseChanged(pageOverlayState, previousState, options.sourceEvent)
	}

	return cloneState(pageOverlayState)
}

const findVisiblePageOverlay = function () {
	if (typeof jQuery === 'undefined') return null
	const $overlays = jQuery('.lay-overlay.lay-show-overlay, .lay-overlay.display-block-overlay').not('.lay-overlay-closing')
	return $overlays.length ? $overlays.eq(0) : null
}

const syncPageStateAfterClose = function () {
	const $visibleOverlay = findVisiblePageOverlay()
	if ($visibleOverlay && $visibleOverlay.length) {
		setPagePhase(OVERLAY_PHASES.VISIBLE, {
			overlay: $visibleOverlay,
			sourceEvent: 'page_overlay_close_end_remaining_overlay_visible',
		})
		return
	}

	setPagePhase(OVERLAY_PHASES.HIDDEN, { clearMeta: true, sourceEvent: 'page_overlay_close_end' })
}

const installPublicApi = function () {
	if (typeof window === 'undefined' || !window.laytheme) return

	window.laytheme.getPageOverlayState = getPageOverlayState
	window.laytheme.isPageOverlayOpen = isPageOverlayOpen
	window.laytheme.isPageOverlayVisible = isPageOverlayVisible
	window.laytheme.isPageOverlayClosing = isPageOverlayClosing
	installOverlayLifecyclePublicApi()
}

const installLifecycleEventListeners = function () {
	if (lifecycleListenersInstalled) return
	if (typeof window === 'undefined' || !window.laytheme || typeof window.laytheme.on !== 'function') return
	lifecycleListenersInstalled = true

	window.laytheme.on('page_overlay_before_shown', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.OPENING, { overlay: $overlay, sourceEvent: 'page_overlay_before_shown' })
	})

	window.laytheme.on('page_overlay_open_start', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.OPENING, { overlay: $overlay, sourceEvent: 'page_overlay_open_start' })
	})

	window.laytheme.on('page_overlay_display_block', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.OPENING, { overlay: $overlay, sourceEvent: 'page_overlay_display_block' })
	})

	window.laytheme.on('overlayshown', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.OPENING, { overlay: $overlay, sourceEvent: 'overlayshown' })
	})

	window.laytheme.on('overlayshown_animation_ended', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.VISIBLE, { overlay: $overlay, sourceEvent: 'overlayshown_animation_ended' })
	})

	window.laytheme.on('page_overlay_open_end', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.VISIBLE, { overlay: $overlay, sourceEvent: 'page_overlay_open_end' })
	})

	window.laytheme.on('page_overlay_close_start', ($overlay) => {
		setPagePhase(OVERLAY_PHASES.CLOSING, { overlay: $overlay, sourceEvent: 'page_overlay_close_start' })
	})

	window.laytheme.on('overlayhidden', () => {
		setPagePhase(OVERLAY_PHASES.HIDDEN, { clearMeta: true, sourceEvent: 'overlayhidden' })
	})

	window.laytheme.on('page_overlay_close_end', () => {
		syncPageStateAfterClose()
	})
}

const getPageOverlayState = function () {
	return cloneState(pageOverlayState)
}

const isPageOverlayOpen = function () {
	return isOpenPhase(pageOverlayState.phase)
}

const isPageOverlayVisible = function () {
	return pageOverlayState.phase == OVERLAY_PHASES.VISIBLE
}

const isPageOverlayClosing = function () {
	return pageOverlayState.phase == OVERLAY_PHASES.CLOSING || pageOverlayState.phase == OVERLAY_PHASES.DESTROYING
}

const i = function () {
	installPublicApi()
	installLifecycleEventListeners()
}

const page_overlay_lifecycle_controller = {
	i: i,
	setPagePhase: setPagePhase,
	getPageOverlayState: getPageOverlayState,
	isPageOverlayOpen: isPageOverlayOpen,
	isPageOverlayVisible: isPageOverlayVisible,
	isPageOverlayClosing: isPageOverlayClosing,
	installLifecycleEventListeners: installLifecycleEventListeners,
}

export default page_overlay_lifecycle_controller
